import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Globe, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import heroImage from "@/assets/hero-biotech.jpg";

const Hero = () => {
  const { t } = useLanguage();

  // pb-32 leaves room for the fixed LiveChat / WhatsApp widgets so the
  // bottom-row stats are never hidden by the bottom-left chat bubble.
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pb-32 pt-12">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        {/* LCP candidate — eager + fetchPriority=high so the browser
            doesn't wait for layout to discover this is the hero image.
            Alt text carries the Pune-India geo + WHO-GMP qualifier so
            Google Image Search ranks us for "WHO-GMP API manufacturer
            Pune" image queries the same way the canvas + JSON-LD
            target the text search. */}
        {/* fetchpriority=lowercase because React 18 warns loudly for the
            camelCase form ("does not recognize fetchPriority prop") on every
            render. React 19 accepts both; keeping lowercase for now works in
            both versions and stops the console noise that was flooding the
            dev tools panel and pushing real errors out of view. */}
        <img
          src={heroImage}
          alt="WHO-GMP pharmaceutical API manufacturing facility — Longshine Boi-Tech, Pune, Maharashtra, India"
          className="w-full h-full object-cover"
          loading="eager"
          {...({ fetchpriority: "high" } as unknown as Record<string, string>)}
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-primary/80"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center px-4 py-2 bg-accent/20 rounded-full mb-6 backdrop-blur-sm border border-accent/30">
            <Award className="w-4 h-4 text-accent mr-2" />
            <span className="text-accent font-medium">{t('hero.badge')}</span>
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold text-primary-foreground mb-6 leading-tight">
            {t('hero.title.leading')}
            <span className="block text-accent">{t('hero.title.pharmaceutical')}</span>
            <span className="block">{t('hero.title.innovation')}</span>
          </h1>

          <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 max-w-2xl leading-relaxed">
            {t('hero.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Link to="/products">
              <Button size="lg" variant="default" className="bg-accent hover:bg-accent-light text-white text-lg px-8 py-4 h-auto">
                {t('hero.button.explore')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              {/* variant="outline" was overriding our color choices with the
                  default shadcn outline (white bg, dark text) — invisible on
                  the dark hero. Drop the variant and own the styling. */}
              <Button size="lg" className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-primary text-lg px-8 py-4 h-auto">
                {t('hero.button.quote')}
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16">
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start mb-2">
                <Globe className="w-6 h-6 text-accent mr-3" />
                <span className="text-3xl font-bold text-primary-foreground">50+</span>
              </div>
              <p className="text-primary-foreground/80">{t('hero.stats.countries')}</p>
            </div>
            
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start mb-2">
                <Users className="w-6 h-6 text-accent mr-3" />
                <span className="text-3xl font-bold text-primary-foreground">500+</span>
              </div>
              <p className="text-primary-foreground/80">{t('hero.stats.products')}</p>
            </div>
            
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start mb-2">
                <Award className="w-6 h-6 text-accent mr-3" />
                <span className="text-3xl font-bold text-primary-foreground">15+</span>
              </div>
              <p className="text-primary-foreground/80">{t('hero.stats.years')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;